import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { useFormSubmission } from '../hooks/useApi';
import { apiService } from '../services/api';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock,
  Send,
  CheckCircle,
  Building,
  Users,
  MessageSquare,
  Loader2
} from 'lucide-react';
import { mockContactInfo } from '../data/mock';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    companyType: '',
    serviceInterest: '',
    message: ''
  });
  
  const { toast } = useToast();
  const { loading: isSubmitting, error, success, submitForm, resetState } = useFormSubmission();

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    resetState();

    try {
      const result = await submitForm(apiService.submitContactForm, formData);
      
      toast({
        title: "Message Sent Successfully!",
        description: result.message,
        duration: 5000,
      });
      
      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        company: '',
        companyType: '',
        serviceInterest: '',
        message: ''
      });
    } catch (err) {
      toast({
        title: "Error",
        description: error || "There was an issue sending your message. Please try again.",
        variant: "destructive",
        duration: 5000,
      });
    }
  };

  const contactMethods = [
    {
      icon: <Phone className="w-6 h-6 text-blue-600" />,
      title: "Phone",
      value: mockContactInfo.phone,
      description: "Call us for immediate assistance"
    },
    {
      icon: <Mail className="w-6 h-6 text-teal-600" />,
      title: "Email",
      value: mockContactInfo.email,
      description: "Send us a detailed message"
    },
    {
      icon: <MapPin className="w-6 h-6 text-blue-600" />,
      title: "Address",
      value: `${mockContactInfo.address.street}, ${mockContactInfo.address.suite}`,
      description: `${mockContactInfo.address.city}, ${mockContactInfo.address.state} ${mockContactInfo.address.zipCode}`
    },
    {
      icon: <Clock className="w-6 h-6 text-teal-600" />,
      title: "Business Hours",
      value: mockContactInfo.businessHours.weekdays,
      description: mockContactInfo.businessHours.weekends
    }
  ];

  const benefits = [
    {
      icon: <CheckCircle className="w-5 h-5 text-green-500" />,
      text: "Free initial consultation"
    },
    {
      icon: <CheckCircle className="w-5 h-5 text-green-500" />,
      text: "Custom solution proposal"
    },
    {
      icon: <CheckCircle className="w-5 h-5 text-green-500" />,
      text: "24-hour response guarantee"
    },
    {
      icon: <CheckCircle className="w-5 h-5 text-green-500" />,
      text: "No obligation assessment"
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-teal-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <Badge className="mb-4 bg-blue-100 text-blue-700 hover:bg-blue-200">
              Get In Touch
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Let's{' '}
              <span className="bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent">
                Transform Your Operations
              </span>{' '}
              Together
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Ready to optimize your supply chain, working capital and cash flow? Get in touch with our Team for a consultation and discover how PBX Solutions can help your business grow.
            </p>
            
            <div className="flex flex-wrap justify-center gap-6">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-center space-x-2">
                  {benefit.icon}
                  <span className="text-gray-700">{benefit.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form & Info Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-3 lg:gap-12">
            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="border-0 shadow-xl">
                <CardHeader>
                  <CardTitle className="text-2xl font-bold text-gray-900 flex items-center">
                    <MessageSquare className="w-6 h-6 mr-3 text-blue-600" />
                    Send us a Message
                  </CardTitle>
                  <CardDescription className="text-gray-600">
                    Fill out the form below and we'll get back to you within 24 hours.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="firstName">First Name *</Label>
                        <Input
                          id="firstName"
                          value={formData.firstName}
                          onChange={(e) => handleInputChange('firstName', e.target.value)}
                          required
                          placeholder="Enter your first name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="lastName">Last Name *</Label>
                        <Input
                          id="lastName"
                          value={formData.lastName}
                          onChange={(e) => handleInputChange('lastName', e.target.value)}
                          required
                          placeholder="Enter your last name"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="email">Email Address *</Label>
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => handleInputChange('email', e.target.value)}
                          required
                          placeholder="your.email@company.com"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="phone">Phone Number</Label>
                        <Input
                          id="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => handleInputChange('phone', e.target.value)}
                          placeholder="+1 (555) 123-4567"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="company">Company Name *</Label>
                      <Input
                        id="company"
                        value={formData.company}
                        onChange={(e) => handleInputChange('company', e.target.value)}
                        required
                        placeholder="Your Company Name"
                      />
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="companyType">Company Type *</Label>
                        <Select onValueChange={(value) => handleInputChange('companyType', value)}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select company type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="importing">Import Company</SelectItem>
                            <SelectItem value="exporting">Export Company</SelectItem>
                            <SelectItem value="manufacturing">Manufacturing Company</SelectItem>
                            <SelectItem value="logistics">Logistics Company</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="serviceInterest">Service Interest</Label>
                        <Select onValueChange={(value) => handleInputChange('serviceInterest', value)}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select service of interest" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="invoice-auditing">Invoice Auditing</SelectItem>
                            <SelectItem value="data-intelligence">Data Intelligence</SelectItem>
                            <SelectItem value="escalation-handling">Escalation Handling</SelectItem>
                            <SelectItem value="payable-services">Payable Services</SelectItem>
                            <SelectItem value="all-services">All Services</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message">Message *</Label>
                      <Textarea
                        id="message"
                        value={formData.message}
                        onChange={(e) => handleInputChange('message', e.target.value)}
                        required
                        placeholder="Tell us about your business needs and how we can help..."
                        rows={6}
                      />
                    </div>

                    <Button 
                      type="submit" 
                      size="lg" 
                      disabled={isSubmitting}
                      className="w-full bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-700 hover:to-teal-700"
                      data-testid="contact-submit-button"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          Send Message
                          <Send className="ml-2 w-4 h-4" />
                        </>
                      )}
                    </Button>

                    {success && (
                      <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center justify-center gap-2" data-testid="success-message">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <span className="text-green-700 font-medium">Request Sent! We'll get back to you within 24 hours.</span>
                      </div>
                    )}
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Contact Information */}
            <div className="mt-12 lg:mt-0">
              <div className="space-y-6">
                <Card className="border-0 shadow-lg">
                  <CardHeader>
                    <CardTitle className="text-xl font-semibold text-gray-900 flex items-center">
                      <Building className="w-5 h-5 mr-2 text-blue-600" />
                      Get in Touch
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {contactMethods.map((method, index) => (
                      <div key={index} className="flex items-start space-x-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-50 to-teal-50 rounded-lg flex items-center justify-center flex-shrink-0">
                          {method.icon}
                        </div>
                        <div>
                          <div className="font-semibold text-gray-900">{method.title}</div>
                          <div className="text-gray-700">{method.value}</div>
                          <div className="text-sm text-gray-600">{method.description}</div>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-lg">
                  <CardHeader>
                    <CardTitle className="text-xl font-semibold text-gray-900 flex items-center">
                      <Users className="w-5 h-5 mr-2 text-teal-600" />
                      Why Choose Us?
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                      <span className="text-gray-700">15+ years of experience</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                      <span className="text-gray-700">500+ satisfied clients</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                      <span className="text-gray-700">Self-improving accuracy rate</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                      <span className="text-gray-700">24/7 professional support</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                      <span className="text-gray-700">Customized solutions</span>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-lg bg-gradient-to-br from-blue-50 to-teal-50">
                  <CardContent className="p-6 text-center">
                    <div className="text-2xl font-bold text-gray-900 mb-2">Real Time</div>
                    <div className="text-gray-600">Cost Savings Insights</div>
                    <div className="text-sm text-gray-500 mt-2">Generated for our clients</div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Common questions about our services and how we can help your business.
            </p>
          </div>

          <div className="space-y-6">
            {[
              {
                question: "How quickly can you start processing our invoices?",
                answer: "We can typically begin processing your invoices within 5-7 business days after initial setup and integration with your systems."
              },
              {
                question: "What types of companies do you work with?",
                answer: "We primarily serve import/export companies and manufacturing businesses that need professional back-office support for invoice auditing, data management, and payment processing."
              },
              {
                question: "How do you ensure data security and confidentiality?",
                answer: "We employ bank-level security measures including encrypted data transmission, secure cloud storage, and strict access controls. All our staff sign comprehensive confidentiality agreements."
              },
              {
                question: "What are your pricing models?",
                answer: "We offer flexible pricing based on volume and specific service requirements. Contact us for a customized quote tailored to your business needs."
              }
            ].map((faq, index) => (
              <Card key={index} className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold text-gray-900">
                    {faq.question}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">{faq.answer}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;